import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Grid,
  ImageListItemBar,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import ImageListItem, {
  imageListItemClasses,
} from '@mui/material/ImageListItem';
import { getAllActions } from '../../graphql/queries';
import { API } from '../../services/amplifyCompat';
import { styled } from '@mui/material/styles';
import ActionCard from './ActionCard';
import { useLanguageContext } from '../contexts/LanguageContext';
import { useContentTranslationsContext } from '../contexts/ContentTranslationsContext';

const StyledImageListItemBar = styled(ImageListItemBar)`
  .MuiImageListItemBar-title {
    overflow: visible;
    white-space: normal;
    overflow-wrap: break-word;
    font-size: 1.3rem;
    color: #0F172A;
    text-align: center;
  }
`;

const StyledImageListItem = styled(ImageListItem)`
  .MuiImageListItem-img {
    border-radius: 7px;
    height: 278px;
    margin-bottom: 8px;
    width: 100%;
  }
`;

const ManageActions = () => {
  const [actionOptions, setActionOptions] = useState();
  const [defaultValue, setDefaultValue] = useState()
  const [selectedAction, setSelectedAction] = useState();
  const [openActionDialog, setOpenActionDialog] = useState(false);
  const [editAction, setEditAction] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [iconRevision, setIconRevision] = useState(() => Date.now());

  const { language } = useLanguageContext();
  const { contentTranslations } = useContentTranslationsContext();

  useEffect(() => {
    setOpenActionDialog(true);
  }, [selectedAction]);

  const handleClose = () => {
    setOpenActionDialog(false);
    setSelectedAction(null);
    setEditAction(false);
  };

  const translateActionsToFrench = useCallback((actions) => {
    const frenchTranslations = contentTranslations.find((translation) => translation.langCode === 'fr');

    if (!frenchTranslations) {
      setActionOptions(actions);
      return;
    }

    let updatedActions = actions.map((action) => {
      let frenchAction = frenchTranslations.translationJSON?.actions?.find((frenchActionObj) => frenchActionObj.action_id === action.action_id);

      if (!frenchAction) {
        return { ...action };
      }

      return { ...action, ...frenchAction };
    });
    setActionOptions(updatedActions);
  }, [contentTranslations]);

  const translateActionsToSpanish = useCallback((actions) => {
    const spanishTranslations = contentTranslations.find((translation) => translation.langCode === 'es');

    if (!spanishTranslations) {
      setActionOptions(actions);
      return;
    }

    let updatedActions = actions.map((action) => {
      let spanishAction = spanishTranslations.translationJSON?.actions?.find((spanishActionObj) => spanishActionObj.action_id === action.action_id);

      if (!spanishAction) {
        return { ...action };
      }

      return { ...action, ...spanishAction };
    });
    setActionOptions(updatedActions);
  }, [contentTranslations]);

  const getActions = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const res = await API.graphql({
        query: getAllActions,
      });
      const actions = res.data.getAllActions;
      setIconRevision((revision) => revision + 1);
      setDefaultValue(actions)
      switch (language) {
        case 'en':
          setActionOptions(actions);
          break;
        case 'fr':
          translateActionsToFrench(actions);
          break;
        case 'es':
          translateActionsToSpanish(actions);
          break;
        default:
          setActionOptions(actions);
          break;
      }
    } catch (error) {
      console.error('Failed to load actions:', error);
      setFetchError(error);
    } finally {
      setLoading(false);
    }
  }, [language, translateActionsToFrench, translateActionsToSpanish]);

  useEffect(() => {
    getActions();
  }, [getActions]);

  const iconSrc = (action) => {
    if (!action.action_icon) return null;
    const base = action.action_icon;
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}w=248&fit=crop&auto=format&v=${iconRevision}`;
  };

  return (
    <Grid
      item
      sx={{
        minHeight: '50vh'
      }}
    >
      {loading && !actionOptions && <CircularProgress />}
      {fetchError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={getActions}>
              Retry
            </Button>
          }
        >
          Failed to load actions. Please try again.
        </Alert>
      )}
      <Box
        sx={{
          display: 'grid',
          gap: '20px',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
          },
          rowGap: 2,
          [`& .${imageListItemClasses.root}`]: {
            display: 'flex',
            flexDirection: 'column',
          },
          justifyItems: 'center',
        }}
      >
        {actionOptions &&
          actionOptions.map((action, index) => (
            <StyledImageListItem
              key={index}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  opacity: '0.7',
                },
                filter: action.is_hidden && 'grayscale(100%)',
                width: '100%',
              }}
              onClick={() => setSelectedAction(action)}
            >
              {action.action_icon ? (
                <img
                  src={iconSrc(action)}
                  alt={action.action_name}
                  loading="lazy"
                />
              ) : (
                <Box
                  sx={{
                    backgroundColor: '#B4EEB4',
                    borderRadius: '7px',
                    height: 278,
                    width: '100%',
                    marginBottom: '8px',
                  }}
                />
              )}
              <StyledImageListItemBar
                title={action.action_name}
                position="below"
              />
            </StyledImageListItem>
          ))}
        {selectedAction && (
          <ActionCard
          defaultValue={defaultValue}
            action={selectedAction}
            open={openActionDialog}
            handleClose={handleClose}
            editAction={editAction}
            setEditAction={setEditAction}
            getActions={getActions}
            iconRevision={iconRevision}
          />
        )}
      </Box>
    </Grid>
  );
};
export default ManageActions;
